import { useLocation, useSearch } from "wouter";
import {
  FundingDirectory,
  PublicOpenApplications,
  PublicRoundApplication,
} from "@client/components/applications/OpenApplications";
import { invitationReturnPath } from "@lib/teamInvitations";
import { Logo } from "@client/components/logo";
import { Page } from "@client/components/utils/Page";
import { Card, CardContent } from "@sfc/ui";
import { trpc } from "@client/trpc";
import { WelcomeLanding } from "./WelcomeLanding";
import { LoginPanel } from "./LoginButton";

export default function Login() {
  const [pathname] = useLocation();
  const search = useSearch();
  const isInvitation = invitationReturnPath(pathname) !== "/";
  const urlSearchParams = new URLSearchParams(search);
  const params = Object.fromEntries(urlSearchParams.entries());

  const isDirectory = pathname === "/apply" || pathname === "/apply/";
  const applicationRound = /^\/apply\/([^/]+)\/?$/.exec(pathname);

  const { data: welcomeEnabled, isPending } =
    trpc.getLoggedOutLandingEnabled.useQuery(undefined, {
      enabled: !isDirectory && !applicationRound,
      staleTime: 60_000,
      retry: false,
      refetchOnWindowFocus: false,
    });

  if (isDirectory) return <FundingDirectory />;

  if (applicationRound) {
    let roundId = "";
    try {
      roundId = decodeURIComponent(applicationRound[1]!);
    } catch {
      // A damaged URL has no matching round; show the normal unavailable state.
    }
    return (
      <PublicRoundApplication
        roundId={roundId}
        signingIn={urlSearchParams.get("apply") === "1"}
      />
    );
  }

  // Resolve the layout before mounting the form so a late flag response
  // cannot discard an email the visitor has already started typing.
  if (isPending)
    return (
      <Page>
        <p role="status" className="text-center text-sm text-muted-foreground">
          Loading sign-in…
        </p>
      </Page>
    );

  const applications = pathname === "/" ? <PublicOpenApplications /> : null;
  if (welcomeEnabled)
    return <WelcomeLanding error={params.error} applications={applications} />;

  return (
    <Page className="flex flex-col items-center justify-center gap-10">
      {pathname === "/" && <span hidden data-application-theme="green" />}
      <div className="mx-auto flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <Logo />
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold">
              {isInvitation
                ? "You’re invited to a team"
                : "Welcome to S-Process"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isInvitation
                ? "Sign in or create an account with the email that was invited. You’ll review the invitation before joining."
                : "Sign in to your workspace"}
            </p>
          </div>
        </div>

        <Card>
          <CardContent>
            <LoginPanel />
          </CardContent>
        </Card>

        {params.error && (
          <div className="text-center text-sm text-red-500">
            Error: {params.error}
          </div>
        )}
      </div>
      {applications}
    </Page>
  );
}
